import { useState } from "react";
import { supabase, BRAND, BRAND_LIGHT, MUTED, pageTitle, btn, chip, linkBtn, errorText, savedText, LINE, WHITE, INK, SURFACE, TEXT, FAINT, ROW_LINE, HOVER_BG } from "./constants";
import { track } from "./lib/analytics";
import { Icon, BackButton } from "./shared";
import { aiFetch } from "./lib/ai";
import { docxToText } from "./lib/docx";
import { useIsMobile } from "./lib/responsive";

// ─── NUEVA POSICIÓN ───────────────────────────────────────────────────────────
// Rehecha el 09/10 con el mismo esquema que Evaluación: una sola pantalla.
//   Izquierda (fija): el camino rápido. Subir la JD (rellena todo sola) o
//     solo tres campos: puesto, qué buscas en dos líneas y salario.
//   Derecha: el detalle, OPCIONAL, en tres bloques plegados. Después, en el
//     mismo sitio, la JD generada para revisar y (admin) el cuestionario.
// Sin pasos obligatorios ni ventanas emergentes encadenadas. El resto se
// completa luego desde la posición (pestaña Documentos).

// Preguntas fijas del cuestionario (en inglés, como el formulario público)
const FIXED_QUESTIONS = {
  "2.1": (title) => `What do you think are the key elements of a great ${title || "professional in this role"}?`,
  "2.2": () => "Looking at the functions described in the job description, which ones do you feel most comfortable with, and which ones less so?",
  "3.1": () => "What type of environment (and boss) suits you best?",
  "3.2": () => "What do you consider your greatest professional achievements? And your greatest learnings?",
  "3.3": () => "What do you value most in your teams? What has worked best for you when managing people?",
  "4.1": () => "What would your superiors, peers & subordinates say about you?",
  "4.2": () => "What would you like to find in your next professional challenge?",
  "4.3": () => "What are your hobbies and interests?",
  "4.4": () => "Recommend a book or a film/series you enjoy",
};

const buildQualQuestions = (title, q23, q24, q34) => [
  { section: "Personal data", items: [
    { n: "1.1", q: "Name", fixed: true }, { n: "1.2", q: "City", fixed: true },
    { n: "1.3", q: "Current salary / expectation", fixed: true }, { n: "1.4", q: "Notice period", fixed: true },
  ]},
  { section: "Technical fit", items: [
    { n: "2.1", q: FIXED_QUESTIONS["2.1"](title), fixed: true }, { n: "2.2", q: FIXED_QUESTIONS["2.2"](), fixed: true },
    { n: "2.3", q: q23, fixed: false }, { n: "2.4", q: q24, fixed: false },
  ]},
  { section: "Soft skills", items: [
    { n: "3.1", q: FIXED_QUESTIONS["3.1"](), fixed: true }, { n: "3.2", q: FIXED_QUESTIONS["3.2"](), fixed: true },
    { n: "3.3", q: FIXED_QUESTIONS["3.3"](), fixed: true }, { n: "3.4", q: q34, fixed: false },
  ]},
  { section: "About you", items: [
    { n: "4.1", q: FIXED_QUESTIONS["4.1"](), fixed: true }, { n: "4.2", q: FIXED_QUESTIONS["4.2"](), fixed: true },
    { n: "4.3", q: FIXED_QUESTIONS["4.3"](), fixed: true }, { n: "4.4", q: FIXED_QUESTIONS["4.4"](), fixed: true },
  ]},
];

const EMPTY_FORM = {
  positionName: "", lookingFor: "", salaryRange: "", shareWithSalary: false,
  hardSkills: "", softSkills: "", culture: "", experience: "", mainTasks: "", preferredBg: "", sampleProfile: "",
  whatsCool: "", perks: "", shareWithPerks: false, careerPath: "", shareWithCareerPath: false, reportingTo: "", directReports: "",
  hiringManager: "", offLimits: "", interviewer1: "", interviewer2: "", interviewer3: "",
};

const NewPositionView = ({ t, lang = "en", setView, onPositionCreated, selectedClientId = null, isAdmin = false }) => {
  const es = lang === "es";
  const isMobile = useIsMobile();
  const [form, setForm] = useState(EMPTY_FORM);
  const [phase, setPhase] = useState("brief"); // brief | jd | questions
  const [openGroups, setOpenGroups] = useState({});
  const [uploadingJD, setUploadingJD] = useState(false);
  const [uploadedName, setUploadedName] = useState("");
  const [uploadErr, setUploadErr] = useState("");
  const [generatedJD, setGeneratedJD] = useState("");
  const [generatingJD, setGeneratingJD] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [savedPositionId, setSavedPositionId] = useState(null);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [qualQuestions, setQualQuestions] = useState(null);
  const [qEdits, setQEdits] = useState({}); // n → texto editado (fijas y de IA)
  const [savingQuestions, setSavingQuestions] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const callAI = async (body) => (await aiFetch({ body: JSON.stringify(body) })).json();

  // ── Subir la JD: rellena todo lo que encuentre ─────────────────────────────
  const handleJDUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingJD(true); setUploadErr("");
    try {
      let content;
      if (file.type === "application/pdf") {
        const base64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.onerror = () => rej(new Error("Read failed")); r.readAsDataURL(file); });
        content = [{ type: "document", source: { type: "base64", media_type: "application/pdf", data: base64 } }, { type: "text", text: "Extract the job description information from this document and return ONLY valid JSON." }];
      } else if (/\.doc$/i.test(file.name)) {
        // El .doc antiguo es binario: no se puede leer en el navegador
        setUploadErr(es ? "Los .doc antiguos no se pueden leer. Guárdalo como .docx o PDF y vuelve a subirlo." : "Old .doc files can't be read. Save it as .docx or PDF and upload it again.");
        setUploadingJD(false);
        return;
      } else {
        const text = /\.docx$/i.test(file.name) ? await docxToText(file) : await file.text();
        if (!text.trim()) throw new Error("Empty file");
        content = [{ type: "text", text: "Extract from this job description:\n\n" + text }];
      }
      const data = await callAI({
        max_tokens: 1200,
        system: "You are an expert recruiter. Extract structured information from the job description and return ONLY valid JSON with these exact keys: positionName, lookingFor (who they are looking for, max 2 short sentences), hardSkills, softSkills, culture, experience, mainTasks, preferredBg, salaryRange, whatsCool, perks, careerPath, reportingTo, directReports, hiringManager. Leave keys as empty string if not found. No markdown, no explanation, just the JSON object.",
        messages: [{ role: "user", content }],
      });
      const parsed = JSON.parse((data.content?.map(b => b.text || "").join("") || "{}").replace(/```json|```/g, "").trim());
      // Solo rellena lo que estaba vacío: lo escrito a mano manda
      setForm(f => { const n = { ...f }; Object.keys(EMPTY_FORM).forEach(k => { if (typeof parsed[k] === "string" && parsed[k].trim() && !String(f[k] || "").trim()) n[k] = parsed[k].trim(); }); return n; });
      setUploadedName(file.name);
    } catch (err) {
      console.error("JD parse error:", err);
      setUploadErr(es ? "No se pudo leer el archivo. Prueba con otro o rellena los campos a mano." : "Couldn't read the file. Try another one or fill in the fields by hand.");
    }
    setUploadingJD(false);
  };

  // ── Generar la JD ──────────────────────────────────────────────────────────
  const generateJobDescription = async () => {
    setGeneratingJD(true); setPhase("jd"); setSaveError("");
    try {
      const f = form;
      const data = await callAI({
        max_tokens: 1200,
        system: "You are an expert executive recruiter. Generate a concise, professional job description in the same language as the input. Format it with clear sections: Role Overview, Key Responsibilities, Requirements, What We Offer. Keep it under 400 words. Output plain text only, no markdown symbols. Use only the information given; where something is missing, keep it generic rather than inventing specifics.",
        messages: [{ role: "user", content: `Generate a job description for:\nPosition: ${f.positionName}\nWho we are looking for: ${f.lookingFor}\nHard skills: ${f.hardSkills}\nSoft skills: ${f.softSkills}\nCulture fit: ${f.culture}\nYears of experience: ${f.experience}\nMain tasks: ${f.mainTasks}\nPreferred background: ${f.preferredBg}\nSalary range: ${f.salaryRange}\nWhat is cool about the role: ${f.whatsCool}\nPerks: ${f.perks}\nCareer path: ${f.careerPath}\nReporting to: ${f.reportingTo}` }],
      });
      setGeneratedJD(data.content?.map(b => b.text || "").join("") || "");
    } catch (e) {
      setGeneratedJD("");
      setSaveError(es ? "No se pudo generar la descripción. Escríbela a mano o vuelve a intentarlo." : "Couldn't generate the description. Write it by hand or try again.");
    }
    setGeneratingJD(false);
  };

  // ── Cuestionario (solo admin) ──────────────────────────────────────────────
  const generateQualQuestions = async () => {
    setGeneratingQuestions(true); setPhase("questions");
    let q = { q23: "", q24: "", q34: "" };
    try {
      let cultureDoc = "", clientNotes = "";
      if (selectedClientId) {
        const { data: cd } = await supabase.from("culture_docs").select("generated_doc").eq("client_id", selectedClientId).maybeSingle();
        if (cd?.generated_doc) cultureDoc = cd.generated_doc;
        const { data: cl } = await supabase.from("clients").select("client_notes").eq("id", selectedClientId).maybeSingle();
        if (cl?.client_notes) clientNotes = cl.client_notes;
      }
      const data = await callAI({
        max_tokens: 800,
        system: `You are an expert executive recruiter generating qualification questions for a specific role.
You must generate exactly 3 questions and return ONLY valid JSON, no markdown, no explanation.
Return this exact structure: {"q23": "...", "q24": "...", "q34": "..."}

QUESTION GUIDELINES:
- q23 and q24 (Technical Fit): Explore if the candidate has exercised exactly this role before, depth of sector knowledge and relevant network, mastery of critical tools or methodologies, and technical strengths that generate immediate impact. Questions must provoke real concrete examples, not generic opinions. They must be complementary, not redundant.
- q34 (Soft Skills): Identify the 1-2 most critical soft skills for this specific role in this company context that are NOT already covered by these fixed questions: "What type of environment suits you best?", "What are your greatest professional achievements?", "What do you value most in your teams?". The question must explore concrete evidence of the most differential soft skill.

All questions must be specific to this role, not generic.`,
        messages: [{ role: "user", content: `Job Description:\n${generatedJD}\n\nPosition: ${form.positionName}\nWho we are looking for: ${form.lookingFor}\nHard skills required: ${form.hardSkills}\nSoft skills required: ${form.softSkills}\n${cultureDoc ? `\nCompany Culture:\n${cultureDoc}` : ""}${clientNotes ? `\nClient Notes:\n${clientNotes}` : ""}` }],
      });
      const parsed = JSON.parse((data.content?.map(b => b.text || "").join("") || "{}").replace(/```json|```/g, "").trim());
      q = { q23: parsed.q23 || "", q24: parsed.q24 || "", q34: parsed.q34 || "" };
    } catch (e) { console.error("Question generation error:", e); }
    setQualQuestions(buildQualQuestions(form.positionName, q.q23, q.q24, q.q34));
    setQEdits({});
    setGeneratingQuestions(false);
  };

  // Guarda el cuestionario CON las ediciones (antes las de las preguntas
  // fijas se perdían: se podían escribir pero no se guardaban)
  const saveQualQuestions = async () => {
    if (!savedPositionId || !qualQuestions) return;
    setSavingQuestions(true); setSaveError("");
    const finalQuestions = qualQuestions.map(sec => ({ ...sec, items: sec.items.map(it => (qEdits[it.n] != null ? { ...it, q: qEdits[it.n] } : it)) }));
    const { data, error } = await supabase.from("positions").update({ qual_questions: finalQuestions }).eq("id", savedPositionId).select("id");
    setSavingQuestions(false);
    if (error || !data || data.length === 0) { setSaveError(es ? "No se pudo guardar el cuestionario. Vuelve a intentarlo." : "Couldn't save the questionnaire. Please try again."); return; }
    setView("open");
  };

  // ── Abrir la posición ──────────────────────────────────────────────────────
  const confirmPosition = async () => {
    setSaveError(""); setSaving(true);
    const today = new Date();
    const dateOpening = `${String(today.getDate()).padStart(2, "0")}/${String(today.getMonth() + 1).padStart(2, "0")}/${String(today.getFullYear()).slice(2)}`;
    const { data: inserted, error: insertErr } = await supabase.from("positions").insert({
      title: form.positionName || "New Position", job_description: generatedJD, status: "open", form_data: form, client_id: selectedClientId,
    }).select().single();
    setSaving(false);
    if (insertErr) { setSaveError(insertErr.message || (es ? "Error al guardar la posición" : "Error saving position")); return; }
    track("position_created", { position: inserted.title || "" });
    if (onPositionCreated) onPositionCreated({
      id: inserted.id, title: inserted.title, candidates: [], dateOpening,
      metrics: { considered: 0, interested: 0, qualified: 0, validated: 0, pending: 0, live: 0 },
      score: 0, jobDescription: generatedJD, formData: form, status: "open", clientId: selectedClientId,
    });
    setSavedPositionId(inserted.id);
    if (isAdmin) await generateQualQuestions(); else setView("open");
  };

  // ── Estilos (los de la ficha y Evaluación) ─────────────────────────────────
  const card = { background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14 };
  const lbl = { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, display: "block", marginBottom: 6 };
  // Móvil: 16 px de letra (iOS hace zoom por debajo) y 44 px de alto mínimo
  const control = { width: "100%", boxSizing: "border-box", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: isMobile ? 16 : 13, fontFamily: "inherit", color: INK, background: WHITE, outline: "none", padding: "9px 12px", ...(isMobile ? { minHeight: 44, padding: "10px 12px" } : null) };
  const focus = { onFocus: e => e.target.style.borderColor = BRAND, onBlur: e => e.target.style.borderColor = LINE };
  const primary = (disabled) => ({ ...(disabled ? btn.disabled : btn.primary), width: "100%", height: isMobile ? 48 : 40, padding: 0, fontSize: isMobile ? 15 : 13 });
  // Enlace secundario gris (linkBtn común en gris)
  const greyLink = { ...linkBtn, color: MUTED, fontWeight: 600, ...(isMobile ? { minHeight: 40, fontSize: 13 } : null) };
  const spinner = (msg) => (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "28px 20px", fontSize: 13, color: MUTED }}>
      <div style={{ width: 18, height: 18, border: `2.5px solid ${BRAND_LIGHT}`, borderTop: `2.5px solid ${BRAND}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      {msg}
      <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
    </div>
  );
  // "Compartir con el candidato": casilla pequeña bajo el campo
  const share = (key) => (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 6, cursor: "pointer", fontSize: 12, color: form[key] ? BRAND : MUTED, fontWeight: 600, ...(isMobile ? { minHeight: 40, marginTop: 2, gap: 8, fontSize: 13 } : null) }}>
      <input type="checkbox" checked={!!form[key]} onChange={e => set(key, e.target.checked)} style={{ width: isMobile ? 18 : 14, height: isMobile ? 18 : 14, accentColor: BRAND, cursor: "pointer" }} />
      {t.shareWithCandidate}
    </label>
  );

  // ── Detalle opcional: tres bloques, fila etiqueta | campo ─────────────────
  const GROUPS = [
    { id: "profile", title: es ? "Perfil" : "Profile", fields: [
      ["mainTasks", t.mainTasks, "area", es ? "Qué hará en los próximos 6-12 meses" : "What they'll do in the next 6-12 months"],
      ["hardSkills", t.hardSkills, "area", es ? "Experiencia, conocimiento técnico, mercado, idiomas" : "Experience, technical expertise, market, languages"],
      ["softSkills", t.softSkillsLabel, "area", es ? "Empatía, ambición, autoconocimiento…" : "Empathy, hunger, self-awareness…"],
      ["culture", t.culture, "area", es ? "Valores, ritmo, apertura, humildad…" : "Values, pace, openness, humility…"],
      ["experience", t.experience, "input", es ? "p. ej. 5-10" : "e.g. 5-10"],
      ["preferredBg", t.preferredBg, "input", es ? "Big Four, startups, big tech…" : "Big Four, startups, big tech…"],
      ["sampleProfile", t.sampleProfile, "input", "https://linkedin.com/in/…"],
    ]},
    { id: "offer", title: es ? "La oferta" : "The offer", fields: [
      ["whatsCool", t.whatsCool, "area", ""],
      ["perks", t.perks, "area", "", "shareWithPerks"],
      ["careerPath", t.careerPath, "area", "", "shareWithCareerPath"],
      ["reportingTo", t.reportingTo, "input", ""],
      ["directReports", t.directReports, "input", es ? "Sí / No" : "Yes / No"],
    ]},
    { id: "process", title: es ? "El proceso" : "The process", fields: [
      ["hiringManager", t.hiringManager, "input", es ? "Responsable directo" : "Direct supervisor"],
      ["offLimits", t.offLimits, "input", es ? "Empresas o personas a evitar" : "Companies or people to avoid"],
      ["interviewer1", `${t.interviewers} 1`, "input", "email"],
      ["interviewer2", `${t.interviewers} 2`, "input", "email"],
      ["interviewer3", `${t.interviewers} 3`, "input", "email"],
    ]},
  ];

  const detailCard = (
    <div style={{ ...card, overflow: "hidden" }}>
      <div style={{ padding: isMobile ? "14px 16px 12px" : "16px 20px 12px", display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, ...(isMobile ? { flexWrap: "wrap", rowGap: 2 } : null) }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: INK }}>{es ? "Más detalle" : "More detail"}</span>
        <span style={{ fontSize: 12, color: MUTED }}>{es ? "Opcional · se puede completar después" : "Optional · can be filled in later"}</span>
      </div>
      {GROUPS.map(g => {
        const filled = g.fields.filter(([k]) => String(form[k] || "").trim()).length;
        const isOpen = !!openGroups[g.id];
        return (
          <div key={g.id}>
            <button onClick={() => setOpenGroups(o => ({ ...o, [g.id]: !o[g.id] }))}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", background: SURFACE, border: "none", borderTop: `1px solid ${LINE}`, borderBottom: isOpen ? `1px solid ${LINE}` : "none", padding: isMobile ? "0 16px" : "11px 20px", cursor: "pointer", fontFamily: "inherit", ...(isMobile ? { minHeight: 48 } : null) }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 }}>{g.title}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: filled ? TEXT : FAINT }}>
                {filled}/{g.fields.length}
                <span style={{ transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.15s", display: "inline-flex" }}><Icon name="chevronRight" size={14} color={MUTED} /></span>
              </span>
            </button>
            {isOpen && g.fields.map(([k, label, kind, ph, shareKey]) => (
              <div key={k} style={isMobile
                  ? { display: "flex", flexDirection: "column", gap: 6, padding: "12px 16px", borderBottom: `1px solid ${ROW_LINE}` }
                  : { display: "grid", gridTemplateColumns: "minmax(150px, 30%) 1fr", gap: 16, padding: "10px 20px", borderBottom: `1px solid ${ROW_LINE}`, alignItems: "start" }}>
                <span style={isMobile ? { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4 } : { fontSize: 12, color: TEXT, paddingTop: 9 }}>{label}</span>
                <div>
                  {kind === "area"
                    ? <textarea value={form[k]} onChange={e => set(k, e.target.value)} placeholder={ph} rows={2} style={{ ...control, resize: "vertical", lineHeight: 1.5 }} {...focus} />
                    : <input value={form[k]} onChange={e => set(k, e.target.value)} placeholder={ph} style={control} {...focus} />}
                  {shareKey && share(shareKey)}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );

  const jdCard = (
    <div style={{ ...card, padding: isMobile ? "14px 16px 16px" : "18px 20px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 12, ...(isMobile ? { flexWrap: "wrap", rowGap: 2 } : null) }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: INK }}>{es ? "Descripción del puesto" : "Job description"}</span>
        {!generatingJD && <span style={{ fontSize: 12, color: MUTED }}>{es ? "Generada con IA · edítala si hace falta" : "AI generated · edit if needed"}</span>}
      </div>
      {generatingJD ? spinner(es ? "Generando la descripción del puesto..." : "Generating job description...") : (
        <textarea value={generatedJD} onChange={e => setGeneratedJD(e.target.value)} rows={isMobile ? 16 : 20}
          style={{ ...control, lineHeight: 1.7, resize: "vertical", padding: "12px 14px" }} {...focus} />
      )}
    </div>
  );

  const questionsCard = (
    <div style={{ ...card, overflow: "hidden" }}>
      <div style={{ padding: isMobile ? "14px 16px 12px" : "16px 20px 12px", display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, ...(isMobile ? { flexWrap: "wrap", rowGap: 2 } : null) }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: INK }}>{es ? "Cuestionario de cualificación" : "Qualification questionnaire"}</span>
        <span style={{ fontSize: 12, color: MUTED }}>{es ? "Lo que responde el candidato al aplicar · editable" : "What the candidate answers when applying · editable"}</span>
      </div>
      {generatingQuestions || !qualQuestions ? spinner(es ? "Preparando las preguntas..." : "Preparing the questions...") : qualQuestions.map(sec => (
        <div key={sec.section}>
          <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, background: SURFACE, padding: isMobile ? "10px 16px" : "10px 20px", borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}` }}>{sec.section}</div>
          {sec.items.map(it => {
            const locked = it.n.startsWith("1.");
            const value = qEdits[it.n] != null ? qEdits[it.n] : it.q;
            return (
              <div key={it.n} style={{ display: "grid", gridTemplateColumns: isMobile ? "28px minmax(0, 1fr) auto" : "34px 1fr auto", gap: isMobile ? 8 : 10, padding: isMobile ? "8px 16px" : "8px 20px", borderBottom: `1px solid ${ROW_LINE}`, alignItems: "start" }}>
                <span style={{ fontSize: 12, color: FAINT, fontWeight: 700, paddingTop: 7 }}>{it.n}</span>
                {locked
                  ? <span style={{ fontSize: 13, color: MUTED, paddingTop: 6 }}>{it.q}</span>
                  : <textarea value={value} onChange={e => { const v = e.target.value; setQEdits(m => ({ ...m, [it.n]: v })); }} rows={Math.max(isMobile ? 2 : 1, Math.ceil(String(value).length / (isMobile ? 34 : 95)))}
                      placeholder={es ? "Escribe la pregunta" : "Write the question"}
                      style={{ ...control, border: "1.5px solid transparent", padding: "5px 8px", resize: "vertical", lineHeight: 1.5, ...(isMobile ? { border: `1.5px solid ${LINE}`, padding: "8px 10px" } : null) }}
                      onFocus={e => { e.target.style.borderColor = BRAND; }} onBlur={e => { e.target.style.borderColor = isMobile ? LINE : "transparent"; }} />}
                {!it.fixed && <span style={{ ...chip(false), marginTop: 6 }}>{es ? "IA" : "AI"}</span>}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );

  const canGenerate = !!form.positionName.trim() && !uploadingJD;

  return (
    <div style={{ padding: isMobile ? 16 : 32 }}>
      <BackButton onClick={() => setView("open")} label={t.back} ariaLabel={t.back} style={{ display: "flex", marginBottom: isMobile ? 4 : 16 }} />
      <h1 style={pageTitle(isMobile, isMobile ? 16 : 20)}>{t.newPosition}</h1>

      {/* Móvil: una sola columna. En "brief" va primero lo esencial; al revisar
          la JD o el cuestionario, primero el texto y debajo la acción. */}
      <div style={isMobile
          ? { display: "flex", flexDirection: "column", gap: 12 }
          : { display: "grid", gridTemplateColumns: "minmax(300px, 360px) minmax(0, 1fr)", gap: 16, alignItems: "start" }}>

        {/* ── Izquierda: lo esencial ── */}
        <div style={isMobile
            ? { ...card, padding: "16px 16px 18px", boxSizing: "border-box", order: phase === "brief" ? 0 : 1 }
            : { ...card, padding: "20px 20px 22px", position: "sticky", top: 16, maxHeight: "calc(100vh - 32px)", overflowY: "auto", boxSizing: "border-box" }}>
          {phase === "questions" ? (
            <>
              <div style={{ fontSize: 15, fontWeight: 800, color: INK, marginBottom: 6 }}>{form.positionName}</div>
              <div style={{ ...savedText, marginBottom: 14 }}>✓ {es ? "Posición abierta" : "Position open"}</div>
              <p style={{ fontSize: 13, color: TEXT, lineHeight: 1.6, margin: "0 0 16px" }}>
                {es ? "Último paso: revisa el cuestionario que responderán los candidatos y publícalo." : "Last step: review the questionnaire candidates will answer, and publish it."}
              </p>
              <button onClick={saveQualQuestions} disabled={savingQuestions || generatingQuestions} style={primary(savingQuestions || generatingQuestions)}>
                {savingQuestions ? (es ? "Guardando..." : "Saving...") : (es ? "Publicar cuestionario" : "Publish questionnaire")}
              </button>
              <button onClick={() => setView("open")} style={{ ...greyLink, marginTop: 12 }}>{es ? "Publicar más tarde" : "Publish later"}</button>
            </>
          ) : (
            <>
              {/* Camino principal: subir la JD */}
              <label style={{ display: "block", border: `1.5px dashed ${uploadedName ? LINE : BRAND}`, borderRadius: 12, padding: isMobile ? "18px 16px" : "16px", textAlign: "center", cursor: uploadingJD ? "wait" : "pointer", background: uploadedName ? SURFACE : HOVER_BG, marginBottom: 6 }}>
                <input type="file" accept=".txt,.pdf,.doc,.docx" onChange={handleJDUpload} style={{ display: "none" }} disabled={uploadingJD} />
                <div style={{ fontSize: 13, fontWeight: 700, color: uploadedName ? TEXT : BRAND }}>
                  {uploadingJD ? (es ? "Leyendo la descripción..." : "Reading the description...") : uploadedName ? `✓ ${uploadedName}` : (es ? "Sube la descripción del puesto" : "Upload the job description")}
                </div>
                <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
                  {uploadedName ? (es ? "Campos rellenados. Revísalos o sube otra." : "Fields filled in. Review them or upload another.") : (es ? "PDF, Word o texto · rellena todo lo que encuentre" : "PDF, Word or text · fills in whatever it finds")}
                </div>
              </label>
              {uploadErr && <div style={{ ...errorText, marginBottom: 6 }}>{uploadErr}</div>}

              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0" }}>
                <div style={{ flex: 1, height: 1, background: LINE }} />
                <span style={{ fontSize: 11, color: FAINT }}>{es ? "o lo básico a mano" : "or the basics by hand"}</span>
                <div style={{ flex: 1, height: 1, background: LINE }} />
              </div>

              <label style={lbl}>{t.positionName} *</label>
              <input value={form.positionName} onChange={e => set("positionName", e.target.value)} placeholder={es ? "p. ej. CFO" : "e.g. CFO"} style={{ ...control, marginBottom: 14 }} {...focus} />

              <label style={lbl}>{es ? "Qué buscas" : "Who you're looking for"}</label>
              <textarea value={form.lookingFor} onChange={e => set("lookingFor", e.target.value)} rows={2}
                placeholder={es ? "En dos líneas: el perfil y lo que tiene que resolver" : "In two lines: the profile and what they need to solve"}
                style={{ ...control, resize: "vertical", lineHeight: 1.5, marginBottom: 14 }} {...focus} />

              <label style={lbl}>{t.salaryRange}</label>
              <input value={form.salaryRange} onChange={e => set("salaryRange", e.target.value)} placeholder={es ? "p. ej. 90-110k + bonus" : "e.g. 90-110k + bonus"} style={control} {...focus} />
              {share("shareWithSalary")}

              <div style={{ marginTop: 20 }}>
                {phase === "brief" ? (
                  <button onClick={generateJobDescription} disabled={!canGenerate} style={primary(!canGenerate)}>
                    {es ? "Generar descripción del puesto" : "Generate job description"}
                  </button>
                ) : (
                  <>
                    <button onClick={confirmPosition} disabled={generatingJD || saving || !generatedJD.trim()} style={primary(generatingJD || saving || !generatedJD.trim())}>
                      {saving ? (es ? "Abriendo..." : "Opening...") : (es ? "Abrir posición" : "Open position")}
                    </button>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: isMobile ? 6 : 12, ...(isMobile ? { gap: 10 } : null) }}>
                      <BackButton onClick={() => { if (!generatingJD) setPhase("brief"); }} label={es ? "Volver al detalle" : "Back to detail"} ariaLabel={es ? "Volver al detalle" : "Back to detail"} style={{ fontSize: 12 }} />
                      <button onClick={generateJobDescription} disabled={generatingJD} style={greyLink}>↻ {es ? "Generar otra vez" : "Regenerate"}</button>
                    </div>
                  </>
                )}
                {saveError && <div style={{ ...errorText, marginTop: 10 }}>{saveError}</div>}
                {phase === "brief" && <div style={{ fontSize: 11, color: MUTED, marginTop: 10, lineHeight: 1.5 }}>{es ? "Solo el nombre es obligatorio. Todo lo demás mejora la descripción y se puede completar después desde la posición." : "Only the name is required. Everything else improves the description and can be completed later from the position."}</div>}
              </div>
            </>
          )}
        </div>

        {/* ── Derecha: detalle, descripción o cuestionario ── */}
        <div style={{ minWidth: 0, ...(isMobile ? { order: phase === "brief" ? 1 : 0 } : null) }}>
          {phase === "brief" && detailCard}
          {phase === "jd" && jdCard}
          {phase === "questions" && questionsCard}
          {phase === "questions" && saveError && <div style={{ ...errorText, marginTop: 10 }}>{saveError}</div>}
        </div>
      </div>
    </div>
  );
};

export default NewPositionView;
